import React from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { Spin } from "antd";
import pdfjsWorker from "react-pdf/node_modules/pdfjs-dist/build/pdf.worker.entry";
pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorker;

/**
 * @typedef {Object} PDFViewerProps
 * @property {string|File|ArrayBuffer} pdfFile - The PDF file to display. Can be a URL string, File object, or ArrayBuffer
 * @property {number} currentPage - The current page number to display
 * @property {number} scale - The zoom level of the PDF document
 * @property {function} onLoadSuccess - Callback fired when PDF document is successfully loaded
 * @property {function} onLoadError - Callback fired when PDF document fails to load
 * @property {function} onPageLoadSuccess - Callback fired when a page is successfully loaded
 * @property {React.ReactNode} [children] - Optional child elements to render over the PDF
 */

/**
 * A component for displaying PDF documents with zoom and page navigation capabilities.
 * Uses react-pdf for rendering and supports overlay elements via children props.
 *
 * @component
 * @param {PDFViewerProps} props - Component props
 * @returns {React.ReactElement} A PDF viewer component
 *
 * @example
 * ```jsx
 * <PDFViewer
 *   pdfFile="https://example.com/document.pdf"
 *   currentPage={1}
 *   scale={1.5}
 *   onLoadSuccess={(pdf) => console.log(`Loaded ${pdf.numPages} pages`)}
 *   onLoadError={(error) => console.error('Failed to load PDF:', error)}
 *   onPageLoadSuccess={(page) => console.log(`Page ${page.pageNumber} loaded`)}
 * >
 *   <div style={{ position: 'absolute' }}>Overlay content</div>
 * </PDFViewer>
 * ```
 */
const PDFViewer = ({
  pdfFile,
  currentPage,
  scale,
  onLoadSuccess,
  onLoadError,
  onPageLoadSuccess,
  children,
}) => (
  <Document
    file={pdfFile}
    onLoadSuccess={onLoadSuccess}
    onLoadError={onLoadError}
    loading={<Spin size="large" />}
  >
    <Page
      pageNumber={currentPage}
      scale={scale}
      renderAnnotationLayer={false}
      renderTextLayer={false}
      loading={<Spin />}
      width={595.28 * scale}
      height={841.89 * scale}
      onLoadSuccess={onPageLoadSuccess}
    />
    {children}
  </Document>
);

export default PDFViewer;
